// ---------------------------------------------------------------------------
// Digital Cemetery — Star claiming
//
// A star can belong to exactly one soul, forever. The Firestore transaction
// below is what guarantees it: the claim document's ID *is* the star ID, so
// existence == claimed, and two simultaneous claims cannot both succeed.
//
// Collections:
//   claimedStars/{starId}  — the uniqueness ledger + tooltip display data
//   memorials/{id}.galaxyLocation — denormalized onto the memorial
//
// NOTE: This module is pure Firestore + math — no Three.js — so it is safe
// to import from MemorialForm without bloating the main bundle.
// ---------------------------------------------------------------------------

import { doc, runTransaction, serverTimestamp, deleteField } from 'firebase/firestore';
import { starFromIndex } from './generator';

export function starIndexFromId(starId) {
  const n = parseInt(String(starId).replace('star_', ''), 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Claim a star for a memorial. Throws with a human-readable message if the
 * star is already claimed, the memorial already has a star, or the caller
 * doesn't own the memorial.
 */
export async function claimStar(db, { starId, memorialId, memorialSlug, uid, starName, living = false }) {
  const index = starIndexFromId(starId);
  if (index === null) throw new Error('That star could not be identified.');
  if (!uid) throw new Error('You must be signed in to claim a star.');
  if (!memorialId) throw new Error('No memorial selected.');

  const star = starFromIndex(index);
  const claimRef = doc(db, 'claimedStars', starId);
  const memorialRef = doc(db, 'memorials', memorialId);
  const cleanName = (starName || '').trim() || 'A Remembered Star';

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(claimRef);
    if (existing.exists()) {
      throw new Error('This star was just claimed by another memorial. Please choose a neighboring star.');
    }
    const memorialSnap = await tx.get(memorialRef);
    if (!memorialSnap.exists()) throw new Error('Memorial not found.');
    const memorial = memorialSnap.data();
    if (memorial.createdBy !== uid) {
      throw new Error('Only the creator of this memorial can claim its star.');
    }
    if (memorial.galaxyLocation && memorial.galaxyLocation.starId) {
      throw new Error('This memorial already has a star in the galaxy.');
    }

    tx.set(claimRef, {
      memorialId,
      memorialSlug: memorialSlug || memorial.slug || null,
      starName: cleanName,
      firstName: memorial.firstName || null,
      lastName: memorial.lastName || null,
      birthDate: memorial.birthDate || null,
      deathDate: memorial.deathDate || null,
      region: star.region,
      x: star.x,
      y: star.y,
      z: star.z,
      claimedBy: uid,
      claimedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      living: !!living,
    });

    tx.update(memorialRef, {
      galaxyLocation: {
        starId,
        starName: cleanName,
        region: star.region,
        x: star.x,
        y: star.y,
        z: star.z,
        claimedBy: uid,
        autoAssigned: false,
      },
      updatedAt: serverTimestamp(),
    });
  });

  return { starId, starName: cleanName, ...star };
}

/** Rename a claimed star. Only the claimer may do this. */
export async function renameStar(db, { starId, uid, newName }) {
  const cleanName = (newName || '').trim();
  if (!cleanName) throw new Error('Please enter a name for the star.');
  const claimRef = doc(db, 'claimedStars', starId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(claimRef);
    if (!snap.exists()) throw new Error('This star is not claimed.');
    const claim = snap.data();
    if (claim.claimedBy !== uid) throw new Error('Only the claimer can rename this star.');
    tx.update(claimRef, { starName: cleanName, updatedAt: serverTimestamp() });
    if (claim.memorialId) {
      tx.update(doc(db, 'memorials', claim.memorialId), {
        'galaxyLocation.starName': cleanName,
        updatedAt: serverTimestamp(),
      });
    }
  });

  return cleanName;
}

/** Release a star back to the galaxy. Only the claimer may do this. */
export async function releaseStar(db, { starId, uid }) {
  const claimRef = doc(db, 'claimedStars', starId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(claimRef);
    if (!snap.exists()) throw new Error('This star is not claimed.');
    const claim = snap.data();
    if (claim.claimedBy !== uid) throw new Error('Only the claimer can release this star.');
    tx.delete(claimRef);
    tx.set(doc(db, 'claimTombstones', starId), { at: serverTimestamp() });
    if (claim.memorialId) {
      tx.update(doc(db, 'memorials', claim.memorialId), {
        galaxyLocation: deleteField(),
        updatedAt: serverTimestamp(),
      });
    }
  });
}

/**
 * Activate a Forever (pre-need) memorial: the time has come.
 * Sets the date of passing on the memorial, flips it from 'living' to
 * 'activated', and turns the star gold (living: false on the claim).
 * May be performed by the owner or by a designated steward (whose email
 * appears in memorial.stewardEmails — enforced both here and in the
 * Firestore rules).
 */
export async function activateForeverMemorial(db, { memorialId, starId, user, deathDate }) {
  if (!user) throw new Error('You must be signed in.');
  if (!memorialId) throw new Error('Memorial not found.');
  if (!deathDate) throw new Error('Please provide the date of passing.');

  const memorialRef = doc(db, 'memorials', memorialId);
  const claimRef = starId ? doc(db, 'claimedStars', starId) : null;
  const email = (user.email || '').toLowerCase();

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(memorialRef);
    if (!snap.exists()) throw new Error('Memorial not found.');
    const m = snap.data();
    const isOwner = m.createdBy === user.uid;
    const isSteward = Array.isArray(m.stewardEmails) && email && m.stewardEmails.includes(email);
    if (!isOwner && !isSteward) {
      throw new Error('Only the owner or a designated steward can activate this memorial.');
    }
    if (m.preNeedStatus === 'activated') {
      throw new Error('This memorial has already been activated.');
    }

    // All reads must precede writes in a Firestore transaction.
    const claimSnap = claimRef ? await tx.get(claimRef) : null;

    tx.update(memorialRef, {
      deathDate,
      preNeedStatus: 'activated',
      activatedAt: serverTimestamp(),
      activatedBy: user.uid,
      updatedAt: serverTimestamp(),
    });

    if (claimSnap && claimSnap.exists()) {
      tx.update(claimRef, { living: false, deathDate, updatedAt: serverTimestamp() });
    }
  });
}

/**
 * Move a soul to a different star: releases the old star and claims the new
 * one in a single transaction, so the soul is never left starless and the
 * destination can't be double-claimed.
 */
export async function moveStar(db, { fromStarId, toStarId, uid }) {
  const toIndex = starIndexFromId(toStarId);
  if (toIndex === null) throw new Error('That star could not be identified.');
  const toStar = starFromIndex(toIndex);

  const fromRef = doc(db, 'claimedStars', fromStarId);
  const toRef = doc(db, 'claimedStars', toStarId);

  await runTransaction(db, async (tx) => {
    const fromSnap = await tx.get(fromRef);
    if (!fromSnap.exists()) throw new Error('Your star could not be found.');
    const claim = fromSnap.data();
    if (claim.claimedBy !== uid) throw new Error('Only the claimer can move this star.');

    const toSnap = await tx.get(toRef);
    if (toSnap.exists()) {
      throw new Error('That star was just claimed by another memorial. Please choose a different one.');
    }

    tx.delete(fromRef);
    tx.set(doc(db, 'claimTombstones', fromStarId), { at: serverTimestamp() });
    tx.set(toRef, {
      ...claim,
      region: toStar.region,
      x: toStar.x,
      y: toStar.y,
      z: toStar.z,
      claimedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (claim.memorialId) {
      tx.update(doc(db, 'memorials', claim.memorialId), {
        galaxyLocation: {
          starId: toStarId,
          starName: claim.starName,
          region: toStar.region,
          x: toStar.x,
          y: toStar.y,
          z: toStar.z,
          claimedBy: uid,
          autoAssigned: false,
        },
        updatedAt: serverTimestamp(),
      });
    }
  });

  return { starId: toStarId, ...toStar };
}
